// PageView: fetches /api/pages/{folder}/data, lays out visuals at their
// absolute (x,y,width,height) inside a scaled canvas at the page's aspect
// ratio.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { usePage } from "../api/hooks";
import { getVisualRenderer } from "../visuals/registry";
import { useFilterStore, type FilterEntry, type FilterKey } from "../state/filterStore";
import type { VisualPayload } from "../types/pbi";
import { VisualErrorBoundary } from "../App";
// P1-tooltip: PBI canvas-tooltip popover. Mounted by VisualOnCanvas so the
// hover trigger covers every visual type (chart, card, table, slicer…),
// not just chart-shelled ones.
import { TooltipPagePopover } from "../visuals/TooltipPagePopover";
import "./PageView.css";

// P1-tooltip: matches PBI's ~300ms hover delay before the canvas tooltip
// page opens.
const TOOLTIP_HOVER_MS = 300;

// LEGACY-BANNER: only the HSQ folder has a polished /dashboard equivalent,
// so the warning banner is gated to that folder ID. Other PBI pages render
// unchanged.
const HSQ_LEGACY_FOLDER = "ReportSection80ff6d94fed4659c4a06";

export function PageView() {
  const { folder } = useParams<{ folder: string }>();
  // P0: subscribe to the whole filter map so usePage's queryKey changes (and
  // we re-fetch /pages/{folder}/data with ?filters=…) whenever any visual
  // emits a cross-filter click or any slicer mutates the store. selectByVisual
  // still runs per-visual below so a visual never filters itself.
  const filters = useFilterStore((s) => s.filters);
  const { data, isLoading, error } = usePage(folder, filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const selectByVisual = useFilterStore((s) => s.selectByVisual);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState<{ left: number; top: number }>(
    { left: 0, top: 0 },
  );

  const pageW = data?.page.width ?? 1400;
  const pageH = data?.page.height ?? 1650;

  // Fit the scaled canvas to the container width (height auto-flows so the
  // user can scroll long PBI pages — matches PBI's "FitToWidth" behavior).
  // AGENT-L: outer .pageview now carries 16px padding on all sides so we
  // no longer need an extra in-canvas margin offset. clientWidth already
  // excludes padding so we just fit the full inner width.
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const compute = () => {
      const w = el.clientWidth;
      const s = Math.max(0.1, Math.min(2, w / pageW));
      setScale(s);
      setCanvasOffset({ left: 0, top: 0 });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pageW]);

  // Reset scroll when page changes.
  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [folder]);

  if (isLoading) {
    return <div className="pageview-state">Loading page…</div>;
  }
  if (error) {
    return (
      <div className="pageview-state pageview-state-error">
        Failed to load page data: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  if (!data) {
    return <div className="pageview-state">No data</div>;
  }

  // V2-skip-unknown: PBI visualGroups / structural containers extract as
  // type="unknown" with no `fields` bucket. They render as ugly placeholders
  // AND, because they often cover full-page extents at a high z-index, can
  // intercept clicks meant for the real visuals underneath. Drop them from
  // the iterator entirely — they must not appear in the DOM at all. Visuals
  // that are genuinely "unknown" but still carry a data binding (i.e.
  // `fields` has keys) are kept so the unknown-renderer can show a diagnostic
  // placeholder instead of swallowing user-meaningful data silently.
  const visuals = [...data.visuals]
    .filter((vp) => {
      if (vp.spec.type !== "unknown") return true;
      const fields = vp.spec.fields;
      const hasFields = !!fields && Object.keys(fields).length > 0;
      return hasFields;
    })
    .sort((a, b) => (a.spec.position.z ?? 0) - (b.spec.position.z ?? 0));

  const scaledW = pageW * scale;
  const scaledH = pageH * scale;

  const showLegacyBanner = folder === HSQ_LEGACY_FOLDER;

  return (
    <div className="pageview" ref={containerRef}>
      {showLegacyBanner ? (
        <div className="legacy-banner">
          <strong>Legacy view</strong> — this is a generic Power-BI spec rendering and may not match the polished dashboard.
          <a href="/dashboard"> View the new HSQ Dashboard →</a>
        </div>
      ) : null}
      <div
        className="pageview-canvas-host"
        style={{
          width: scaledW,
          height: scaledH,
          marginLeft: canvasOffset.left,
          marginTop: canvasOffset.top,
        }}
      >
        <div
          className="pageview-canvas"
          style={{
            width: pageW,
            height: pageH,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {visuals.map((vp) => (
            <VisualOnCanvas
              key={vp.spec.id}
              payload={vp}
              addFilter={addFilter}
              selectByVisual={selectByVisual}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface VisualOnCanvasProps {
  payload: VisualPayload;
  addFilter: (key: FilterKey, entry: FilterEntry) => void;
  selectByVisual: (visualId: string) => Record<FilterKey, FilterEntry>;
}

function VisualOnCanvas({
  payload,
  addFilter,
  selectByVisual,
}: VisualOnCanvasProps) {
  const Renderer = getVisualRenderer(payload.spec.type);
  const { x, y, width, height, z } = payload.spec.position;
  const filters = selectByVisual(payload.spec.id);

  // P1-tooltip: hover state drives the canvas-tooltip-page popover. We attach
  // mouse handlers to the position-wrapper so the trigger works for every
  // visual type, not just chart-shelled ones (cards, tables, slicers, custom
  // visuals all participate). A 300ms debounce mirrors PBI's hover-to-open
  // behavior and prevents flicker on quick mouse traversal.
  const tooltipFolder = payload.spec.tooltipPageFolder || null;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const hoverTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    };
  }, []);

  // P1-tooltip: hover handlers are no-ops when the visual has no tooltip-page
  // binding, so the overwhelming majority of visuals pay zero hover cost.
  const handleEnter = tooltipFolder
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        setTooltipAnchor({ x: e.clientX, y: e.clientY });
        if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
        hoverTimer.current = window.setTimeout(() => {
          setTooltipVisible(true);
        }, TOOLTIP_HOVER_MS);
      }
    : undefined;
  const handleMove = tooltipFolder
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        setTooltipAnchor({ x: e.clientX, y: e.clientY });
      }
    : undefined;
  const handleLeave = tooltipFolder
    ? () => {
        if (hoverTimer.current) {
          window.clearTimeout(hoverTimer.current);
          hoverTimer.current = null;
        }
        setTooltipVisible(false);
      }
    : undefined;

  return (
    <div
      className="pageview-visual"
      style={{
        left: x,
        top: y,
        width: width,
        height: height,
        zIndex: z,
      }}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <VisualErrorBoundary
        label={`${payload.spec.type} ${payload.spec.id}`}
      >
        <Renderer
          spec={payload.spec}
          data={payload.data}
          filters={filters}
          onFilter={addFilter}
        />
      </VisualErrorBoundary>
      {/* P1-tooltip: portal-mounted popover so it can break out of the
          page's transform-scaled canvas and render at real screen px. */}
      {tooltipFolder ? (
        <TooltipPagePopover
          folder={tooltipFolder}
          anchor={tooltipAnchor}
          visible={tooltipVisible}
        />
      ) : null}
    </div>
  );
}
